import {createSmokingSession} from '../journal/session.js';
import {createSmokingItem} from '../journal/item.js';
import {createItemPreparation} from '../journal/preparation.js';
import {SMOKING_STAGE_TYPES,createSmokingStage} from '../journal/stage.js';
import {createStageTarget,createStageItemTarget} from '../journal/stage-target.js';
import {createSessionWood} from '../journal/wood-mix.js';
import {createRecipe,recipeTranslation} from './recipe.js';
import {createRecipeItem,createRecipeItemPreparation} from './recipe-item.js';

const STAGE_TYPES=new Set(SMOKING_STAGE_TYPES);
const CODE=/^[a-z][a-z0-9_]{1,63}$/;

function required(value,label,max=160){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalText(value,label,max=4000){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function idOrGenerated(value){
  const id=String(value??'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function order(value){
  const number=Number(value??0);
  if(!Number.isInteger(number)||number<0||number>1000000)throw new TypeError('sortOrder is invalid');
  return number;
}

function woodCode(value){
  const code=String(value??'').trim().toLowerCase();
  if(!CODE.test(code))throw new TypeError('Invalid woodKey');
  return code;
}

function share(value){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number)||number<=0||number>100)throw new TypeError('sharePercent must be greater than 0 and at most 100');
  return number;
}

function nextId(idFactory,kind,sourceId){
  if(typeof idFactory!=='function')throw new TypeError('idFactory is required');
  const id=String(idFactory(kind,sourceId)||'').trim();
  if(!id)throw new TypeError('idFactory must return an id');
  return id;
}

export function createRecipeWood({
  id,
  recipeId,
  woodKey,
  name,
  sharePercent=null,
  sortOrder=0
}={}){
  return Object.freeze({
    id:idOrGenerated(id),
    recipeId:required(recipeId,'recipeId',128),
    woodKey:woodCode(woodKey),
    name:required(name,'name',160),
    sharePercent:share(sharePercent),
    sortOrder:order(sortOrder)
  });
}

export function createRecipeStage({
  id,
  recipeId,
  typeKey,
  nameOverride=null,
  instructions=null,
  sortOrder=0
}={}){
  const type=String(typeKey??'').trim().toLowerCase();
  if(!STAGE_TYPES.has(type))throw new TypeError('Unsupported smoking stage type');
  const customName=optionalText(nameOverride,'nameOverride',160);
  if(type==='custom'&&!customName)throw new TypeError('nameOverride is required for a custom stage');
  return Object.freeze({
    id:idOrGenerated(id),
    recipeId:required(recipeId,'recipeId',128),
    typeKey:type,
    nameOverride:customName,
    instructions:optionalText(instructions,'instructions',8000),
    sortOrder:order(sortOrder)
  });
}

export function createRecipeStageTarget({
  recipeStageId,
  targetChamberCelsius=null,
  minChamberCelsius=null,
  maxChamberCelsius=null,
  plannedDurationMinutes=null
}={}){
  const stageId=required(recipeStageId,'recipeStageId',128);
  const value=createStageTarget({
    stageId,
    userId:'recipe-model',
    targetChamberCelsius,
    minChamberCelsius,
    maxChamberCelsius,
    plannedDurationMinutes
  });
  return Object.freeze({
    recipeStageId:stageId,
    targetChamberCelsius:value.targetChamberCelsius,
    minChamberCelsius:value.minChamberCelsius,
    maxChamberCelsius:value.maxChamberCelsius,
    plannedDurationMinutes:value.plannedDurationMinutes
  });
}

export function createRecipeStageItemTarget({
  recipeStageId,
  recipeItemId,
  targetInternalCelsius
}={}){
  const stageId=required(recipeStageId,'recipeStageId',128);
  const itemId=required(recipeItemId,'recipeItemId',128);
  const value=createStageItemTarget({
    stageId,
    itemId,
    userId:'recipe-model',
    targetInternalCelsius
  });
  return Object.freeze({
    recipeStageId:stageId,
    recipeItemId:itemId,
    targetInternalCelsius:value.targetInternalCelsius
  });
}

function assertWoodMix(woods){
  const specified=woods.filter(item=>item.sharePercent!=null);
  const total=specified.reduce((sum,item)=>sum+item.sharePercent,0);
  if(total>100.01)throw new TypeError('Recipe wood share total cannot exceed 100%');
  if(woods.length>0&&specified.length===woods.length&&Math.abs(total-100)>0.01){
    throw new TypeError('Fully specified recipe wood mix must equal 100%');
  }
}

export function createRecipePlan({
  recipe,
  items=[],
  preparations=[],
  woods=[],
  stages=[],
  stageTargets=[],
  stageItemTargets=[]
}={}){
  const root=createRecipe(recipe);
  if(!Array.isArray(items)||items.length<1)throw new TypeError('Recipe requires at least one item');
  if(!Array.isArray(stages)||stages.length<1)throw new TypeError('Executable recipe requires at least one stage');

  const normalizedItems=items.map(createRecipeItem).sort((a,b)=>a.sortOrder-b.sortOrder||a.id.localeCompare(b.id));
  const normalizedPreparations=(preparations||[]).map(createRecipeItemPreparation);
  const normalizedWoods=(woods||[]).map(createRecipeWood).sort((a,b)=>a.sortOrder-b.sortOrder||a.id.localeCompare(b.id));
  const normalizedStages=stages.map(createRecipeStage).sort((a,b)=>a.sortOrder-b.sortOrder||a.id.localeCompare(b.id));
  const normalizedTargets=(stageTargets||[]).map(createRecipeStageTarget);
  const normalizedItemTargets=(stageItemTargets||[]).map(createRecipeStageItemTarget);

  for(const item of normalizedItems)if(item.recipeId!==root.id)throw new TypeError('RECIPE_ITEM_RECIPE_MISMATCH');
  for(const wood of normalizedWoods)if(wood.recipeId!==root.id)throw new TypeError('RECIPE_WOOD_RECIPE_MISMATCH');
  for(const stage of normalizedStages)if(stage.recipeId!==root.id)throw new TypeError('RECIPE_STAGE_RECIPE_MISMATCH');

  const itemIds=new Set(normalizedItems.map(item=>item.id));
  const stageIds=new Set(normalizedStages.map(stage=>stage.id));
  for(const prep of normalizedPreparations)if(!itemIds.has(prep.recipeItemId))throw new TypeError('RECIPE_PREPARATION_ITEM_MISMATCH');
  for(const target of normalizedTargets)if(!stageIds.has(target.recipeStageId))throw new TypeError('RECIPE_STAGE_TARGET_MISMATCH');
  for(const target of normalizedItemTargets){
    if(!stageIds.has(target.recipeStageId)||!itemIds.has(target.recipeItemId)){
      throw new TypeError('RECIPE_STAGE_ITEM_TARGET_MISMATCH');
    }
  }
  assertWoodMix(normalizedWoods);

  return Object.freeze({
    recipe:root,
    items:Object.freeze(normalizedItems),
    preparations:Object.freeze(normalizedPreparations),
    woods:Object.freeze(normalizedWoods),
    stages:Object.freeze(normalizedStages),
    stageTargets:Object.freeze(normalizedTargets),
    stageItemTargets:Object.freeze(normalizedItemTargets)
  });
}

export function recipeToJournalDraft(plan,{
  userId,
  idFactory,
  resolveCustomProductId=null,
  locale=null,
  useRequestId=null
}={}){
  const value=createRecipePlan(plan);
  const owner=required(userId,'userId',128);
  const sessionId=nextId(idFactory,'session',value.recipe.id);
  const translation=recipeTranslation(value.recipe,{locale});

  const session=createSmokingSession({
    id:sessionId,
    userId:owner,
    status:'draft',
    title:translation.title,
    recipeId:value.recipe.id,
    recipeVersion:value.recipe.version,
    recipeSourceSnapshot:{
      type:value.recipe.type,
      authorUserId:value.recipe.authorUserId,
      authorName:value.recipe.authorName,
      sourceName:value.recipe.sourceName,
      sourceUrl:value.recipe.sourceUrl
    },
    recipeUseRequestId:useRequestId,
    sourceSessionId:null
  });

  const itemIdMap=new Map();
  const items=value.items.map(item=>{
    const id=nextId(idFactory,'item',item.id);
    itemIdMap.set(item.id,id);
    let customProductId=null;
    if(item.customProductCode){
      if(typeof resolveCustomProductId!=='function')throw new TypeError('resolveCustomProductId is required for custom recipe items');
      customProductId=String(resolveCustomProductId(item.customProductCode)||'').trim();
      if(!customProductId)throw new TypeError('Custom recipe item could not be resolved for this user');
    }
    return createSmokingItem({
      id,
      userId:owner,
      sessionId,
      catalogueProductCode:item.catalogueProductCode,
      customProductId,
      nameSnapshot:item.name,
      quantity:item.referenceQuantity,
      mass:item.referenceMass,
      massUnit:item.massUnit,
      sortOrder:item.sortOrder
    });
  });

  const preparations=value.preparations.map(prep=>createItemPreparation({
    itemId:itemIdMap.get(prep.recipeItemId),
    userId:owner,
    curingMethodKey:prep.curingMethodKey,
    curingMethodCustom:prep.curingMethodCustom,
    curingDurationMinutes:prep.curingDurationMinutes,
    curingNotes:prep.curingNotes,
    marinadeNotes:prep.marinadeNotes,
    spicesNotes:prep.spicesNotes,
    dryingDurationMinutes:prep.dryingDurationMinutes,
    dryingNotes:prep.dryingNotes,
    preparationDurationMinutes:prep.preparationDurationMinutes,
    preparationNotes:prep.preparationNotes
  }));

  const woods=value.woods.map(wood=>createSessionWood({
    id:nextId(idFactory,'wood',wood.id),
    userId:owner,
    sessionId,
    sourceKind:'catalogue',
    woodKey:wood.woodKey,
    inventoryItemId:null,
    nameSnapshot:wood.name,
    sharePercent:wood.sharePercent,
    sortOrder:wood.sortOrder
  }));

  const stageIdMap=new Map();
  const stages=value.stages.map(stage=>{
    const id=nextId(idFactory,'stage',stage.id);
    stageIdMap.set(stage.id,id);
    return createSmokingStage({
      id,
      userId:owner,
      sessionId,
      typeKey:stage.typeKey,
      nameOverride:stage.nameOverride,
      instructions:stage.instructions,
      sortOrder:stage.sortOrder
    });
  });

  const stageTargets=value.stageTargets.map(target=>createStageTarget({
    stageId:stageIdMap.get(target.recipeStageId),
    userId:owner,
    targetChamberCelsius:target.targetChamberCelsius,
    minChamberCelsius:target.minChamberCelsius,
    maxChamberCelsius:target.maxChamberCelsius,
    plannedDurationMinutes:target.plannedDurationMinutes
  }));

  const stageItemTargets=value.stageItemTargets.map(target=>createStageItemTarget({
    stageId:stageIdMap.get(target.recipeStageId),
    itemId:itemIdMap.get(target.recipeItemId),
    userId:owner,
    targetInternalCelsius:target.targetInternalCelsius
  }));

  return Object.freeze({
    session,
    items:Object.freeze(items),
    preparations:Object.freeze(preparations),
    woods:Object.freeze(woods),
    stages:Object.freeze(stages),
    stageTargets:Object.freeze(stageTargets),
    stageItemTargets:Object.freeze(stageItemTargets),
    sourceRecipe:Object.freeze({
      id:value.recipe.id,
      version:value.recipe.version,
      type:value.recipe.type,
      authorUserId:value.recipe.authorUserId,
      sourceName:value.recipe.sourceName,
      sourceUrl:value.recipe.sourceUrl
    })
  });
}
